import { css, keyframes } from 'styled-components';

/**
 * Every looping animation in this page is applied through `motion`, which gates
 * it behind `prefers-reduced-motion: no-preference`. The base styles therefore
 * have to stand on their own as the composed final frame: a reduced-motion user
 * sees the finished cube, a filled shimmer bar and a solid active node rather
 * than an empty box.
 */
export const motion = value => css`
  @media (prefers-reduced-motion: no-preference) {
    animation: ${value};
  }
`;

// Entry — reduced motion keeps the fade and drops the 8px rise.
export const cardFade = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

export const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(0.5rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Progress rail — the active node breathes, the trail grows into place.
export const nodePulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.25); }
`;

export const haloPulse = keyframes`
  0%, 100% {
    opacity: 0.35;
    transform: scale(1);
  }
  50% {
    opacity: 0;
    transform: scale(2.2);
  }
`;

// WAITING_FOR_FILE — a chevron rising into a tray, dots drifting after it.
export const chevronRise = keyframes`
  0% {
    opacity: 0;
    transform: translateY(0.5rem);
  }
  30% { opacity: 1; }
  70% { opacity: 1; }
  100% {
    opacity: 0;
    transform: translateY(-0.75rem);
  }
`;

export const driftUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(0.75rem);
  }
  40% { opacity: 1; }
  100% {
    opacity: 0;
    transform: translateY(-0.75rem);
  }
`;

// QUEUED — pills advancing through a masked track.
export const queueAdvance = keyframes`
  0% {
    opacity: 0;
    transform: translateX(4.5rem);
  }
  15% { opacity: 1; }
  85% { opacity: 1; }
  100% {
    opacity: 0;
    transform: translateX(-4.5rem);
  }
`;

// PROCESSING — the mesh turns while its edges draw themselves in.
export const cubeSpin = keyframes`
  from { transform: rotateY(0deg); }
  to { transform: rotateY(360deg); }
`;

export const drawEdges = keyframes`
  0% { stroke-dashoffset: 260; }
  60% { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 0; }
`;

// Indeterminate — the API reports no percentage, so nothing here implies one.
export const shimmer = keyframes`
  0% { transform: translateX(-120%); }
  100% { transform: translateX(430%); }
`;

// A slow accent sweep across the model's own thumbnail.
export const sheen = keyframes`
  0% { transform: translateX(-120%) skewX(-12deg); }
  60%, 100% { transform: translateX(320%) skewX(-12deg); }
`;

// COMPLETED / READY.
export const drawCheck = keyframes`
  from { stroke-dashoffset: 48; }
  to { stroke-dashoffset: 0; }
`;

export const ringExpand = keyframes`
  0% {
    opacity: 0.6;
    transform: scale(0.6);
  }
  100% {
    opacity: 0;
    transform: scale(1.8);
  }
`;

// Terminal states — one scale-in and a short shake, then completely still.
export const errorPop = keyframes`
  from {
    opacity: 0;
    transform: scale(0.7);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

export const errorShake = keyframes`
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-0.35rem); }
  75% { transform: translateX(0.35rem); }
`;
